/**
 * buildScheduleDraftWorkspaceModels — ScheduleDraft 工作台非画布视图模型投影层。
 *
 * 本模块把草稿详情、只读 ScheduleDraftTrack 与外部参考轨投影为 Timetable、
 * Pattern 和 source track 选择器可消费的本地 ViewModel。它承接工作台多观察模式，
 * 但不处理 HTTP 请求、React 状态或组件交互。
 *
 * 设计文档 → docs/20_workspace_ux/20_multitrack_view.md
 * 骨架关联 → hooks/useScheduleDraftEditorData.js (模型消费), lib/trackUtils.js (轨 key/集合), lib/canvas/buildScheduleDraftCanvasModel.js (画布模型)
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { getScheduleDraftTransportDemands } from "../../constants/transportDemandDefaults";
import { getAgentEvaluationSummary, getAgentTrackDisplayLabel, isAgentTrack } from "../agentTrackUtils";
import { getFlightRouteBlockTime, getFlightRouteFullName, getFlightRouteName, getFlightRouteSummary } from "../flightDataUtils";
import { calculateDateTimeDiffMinutes, formatMinutesAsHHMM, getFlightBlockTimeMinutes, getFlightMinBlockTimeMinutes, getFlightTimeDisplayInfo, normalizeMinutes, valuesEqual, withCalculatedFlightTimings } from "../flightTimingUtils";
import { normalizeAircraftIdentifier, normalizeAirportCode } from "../routeUtils";
import {
    DRAFT_CURRENT_TRACK_KEY,
    applyReferenceDateShift,
    buildScheduleTrackKey,
    getDefaultEditingTrack,
    getDraftFlights,
    getEditingTracks,
    getReferenceDisplayTracks,
    getTrackDisplayName,
    getTrackFlights,
    isExecutionReferenceSource,
    isScheduleTrackKey,
    parseTrackKey,
    toArray,
} from "../trackUtils";
import { validateAllFlightConstraints } from "../validation/flightConstraintValidators";

// index.js/App.jsx 未统一初始化 dayjs 插件，保持模块级 UTC 注册。
dayjs.extend(utc);

const GROUND_TIME_VIOLATION_TYPES = new Set(["H3_MIN_GROUND", "insufficient_ground_time", "ground_time", "missing_turnaround_data"]);
const LOCAL_CONSTRAINT_TYPE_ALIASES = {
    INSUFFICIENT_GROUND_TIME_LOCAL: "insufficient_ground_time",
    MISSING_MIN_GROUND_TIME_LOCAL: "missing_turnaround_data",
    DEP_CURFEW_LOCAL: "dep_curfew",
    ARR_CURFEW_LOCAL: "arr_curfew",
};
const DELETED_CHANGE_STATUS = "DELETED";
const SEGMENT_TIME_MARGIN_HOURS = 6;
const CURRENT_PLAN_LABEL = "当前计划";

const TIMETABLE_PREVIEW_DIFF_FIELDS = ["std", "sta", "block_time_minutes", "ground_time_minutes"];

function isDeletedFlight(flight) {
    return flight?.is_deleted || String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function getExternalReferenceFlights(referenceSource) {
    return applyReferenceDateShift(toArray(referenceSource?.flights).filter((flight) => !flight?.is_deleted), referenceSource);
}

function getConstraintViolationType(violation) {
    return violation?.type ?? violation?.constraint_type ?? violation?.violationType ?? violation?.code ?? null;
}

function getCanonicalConstraintViolationType(violation) {
    const type = getConstraintViolationType(violation);
    return LOCAL_CONSTRAINT_TYPE_ALIASES[type] || type;
}

function getConstraintViolationFlightId(violation) {
    return violation?.flight_id ?? violation?.flightId ?? violation?.entityId ?? null;
}

function addViolationByFlightId(violationsByFlightId, violation, seenKeysByFlightId) {
    const rawFlightId = getConstraintViolationFlightId(violation);
    const flightId = isGroundTimeViolation(violation)
        ? (
            violation?.prev_flight_id
            ?? violation?.previous_flight_id
            ?? violation?.prevFlightId
            ?? violation?.previousFlightId
            ?? rawFlightId
        )
        : rawFlightId;
    if (flightId === undefined || flightId === null || flightId === "") {
        return;
    }

    const key = String(flightId);
    const violationType = getCanonicalConstraintViolationType(violation) || "constraint_violation";
    const seenKeys = seenKeysByFlightId.get(key) || new Set();
    if (seenKeys.has(violationType)) {
        return;
    }

    seenKeys.add(violationType);
    seenKeysByFlightId.set(key, seenKeys);
    const existing = violationsByFlightId.get(key) || [];
    violationsByFlightId.set(key, [...existing, violation]);
}

function normalizeLocalValidationIssue(issue) {
    const flightId = getConstraintViolationFlightId(issue);
    const type = getConstraintViolationType(issue);
    if (flightId === undefined || flightId === null || flightId === "" || !type) {
        return null;
    }

    return {
        ...issue,
        flight_id: flightId,
        flightId,
        type,
        constraint_type: issue?.constraint_type || type,
        violationType: type,
        severity: issue?.severity || "warning",
        source: issue?.source || "client",
        message: issue?.message || type,
    };
}

function collectConstraintViolationsByFlight(analysisResult, localValidationIssues = []) {
    const violationsByFlightId = new Map();
    const seenKeysByFlightId = new Map();
    const constraintViolations = analysisResult?.constraint_violations || {};

    Object.values(constraintViolations).forEach((violations = []) => {
        toArray(violations).forEach((violation) => {
            addViolationByFlightId(violationsByFlightId, violation, seenKeysByFlightId);
        });
    });

    toArray(localValidationIssues).forEach((issue) => {
        const violation = normalizeLocalValidationIssue(issue);
        if (violation) {
            addViolationByFlightId(violationsByFlightId, violation, seenKeysByFlightId);
        }
    });

    return violationsByFlightId;
}

function isGroundTimeViolation(violation) {
    const type = getConstraintViolationType(violation);
    return GROUND_TIME_VIOLATION_TYPES.has(type) || GROUND_TIME_VIOLATION_TYPES.has(getCanonicalConstraintViolationType(violation));
}

function getGroundTimeViolation(violations = []) {
    return toArray(violations).find((violation) => isGroundTimeViolation(violation)) || null;
}

function buildDerivedGroundTimeViolation(flight, flightId, nextFlightSummary = null) {
    const actualMinutes = normalizeMinutes(flight?.ground_time_minutes);
    const requiredMinutes = normalizeMinutes(flight?.min_ground_time_minutes);
    if (actualMinutes === null || requiredMinutes === null || actualMinutes >= requiredMinutes) {
        return null;
    }

    return {
        flight_id: flightId,
        prev_flight_id: flightId,
        previous_flight_id: flightId,
        next_flight_id: nextFlightSummary?.id ?? null,
        affected_flight_ids: [flightId, nextFlightSummary?.id].filter(Boolean),
        type: "insufficient_ground_time",
        constraint_type: "H3_MIN_GROUND",
        actual_minutes: actualMinutes,
        required_minutes: requiredMinutes,
        field: "ground_time_minutes",
        field_label: "当前航班过站时间",
        source: "timetable-row",
        message: `过站时间不足：实际 ${actualMinutes}min，要求 ${requiredMinutes}min`,
    };
}

function normalizeCalculationNotes(flight, flightId) {
    const rawNotes = toArray(flight?._calculation_notes ?? flight?.calculation_notes);
    return rawNotes.map((note, index) => {
        const delayMinutes = normalizeMinutes(note?.delay_minutes ?? note?.delayMinutes);
        const flightLabel = note?.flight_number || note?.flightNumber || flight?.flight_number || flightId;
        return {
            ...note,
            flight_id: note?.flight_id ?? note?.flightId ?? flightId,
            flightId: note?.flightId ?? note?.flight_id ?? flightId,
            type: note?.type || "auto_calculation_delay",
            constraint_type: note?.constraint_type || "AUTO_CALC_DELAY",
            severity: note?.severity || "info",
            source: note?.source || "auto-calculate",
            status_label: note?.status_label || "顺延",
            constraint_label: note?.constraint_label || "自动顺延",
            delay_minutes: delayMinutes,
            time_basis: note?.time_basis || "UTC",
            message: note?.message || `自动计算将 ${flightLabel} 顺延${delayMinutes === null ? "" : ` ${delayMinutes} 分钟`}`,
            _noteIndex: index,
        };
    });
}

function summarizeAdjacentFlight(flight) {
    if (!flight) {
        return null;
    }

    return {
        id: getFlightId(flight),
        flightNumber: flight.flight_number || flight.flightNumber || null,
        departureAirport: flight.departure_airport || flight.departureAirport || null,
        arrivalAirport: flight.arrival_airport || flight.arrivalAirport || null,
        std: flight.std || null,
        sta: flight.sta || null,
    };
}

function getFlightId(flight) {
    const flightId = flight?.id ?? flight?.flight_id ?? flight?.flightId;
    return flightId === undefined || flightId === null || flightId === "" ? null : String(flightId);
}

function getTimingGroupKey(flight) {
    const trackId = flight?.track_id === undefined || flight?.track_id === null ? "" : String(flight.track_id).trim();
    const aircraftReg = normalizeAircraftIdentifier(flight?.aircraft_reg);
    if (!trackId || !aircraftReg) {
        return null;
    }
    return `${trackId}::${aircraftReg}`;
}

function isContinuousGroundTurn(previousFlight, nextFlight) {
    const arrivalAirport = normalizeAirportCode(previousFlight?.arrival_airport);
    const departureAirport = normalizeAirportCode(nextFlight?.departure_airport);
    return Boolean(arrivalAirport && departureAirport && arrivalAirport === departureAirport);
}

function buildPreviousFlightIdByFlightId(flights = []) {
    const previousFlightIdByFlightId = new Map();
    const previousFlightByGroupKey = new Map();

    flights.forEach((flight) => {
        const flightId = getFlightId(flight);
        const groupKey = getTimingGroupKey(flight);
        if (!flightId || !groupKey) {
            return;
        }

        const previousFlight = previousFlightByGroupKey.get(groupKey);
        const previousFlightId = getFlightId(previousFlight);
        if (previousFlightId && isContinuousGroundTurn(previousFlight, flight)) {
            previousFlightIdByFlightId.set(flightId, previousFlightId);
        }
        previousFlightByGroupKey.set(groupKey, flight);
    });

    return previousFlightIdByFlightId;
}

function getGroundTimeViolationOwnerFlightId(violation, flightId, previousFlightIdByFlightId = new Map()) {
    const ownerFlightId = violation?.prev_flight_id
        ?? violation?.previous_flight_id
        ?? violation?.prevFlightId
        ?? violation?.previousFlightId
        ?? previousFlightIdByFlightId.get(String(flightId));
    const fallbackFlightId = flightId === undefined || flightId === null || flightId === "" ? null : String(flightId);
    return ownerFlightId === undefined || ownerFlightId === null || ownerFlightId === "" ? fallbackFlightId : String(ownerFlightId);
}

function addGroundTimeViolationByFlightId(violationsByFlightId, flightId, violation) {
    if (!flightId) {
        return;
    }

    const key = String(flightId);
    const existing = violationsByFlightId.get(key) || [];
    violationsByFlightId.set(key, [...existing, violation]);
}

function collectGroundTimeViolationsByFlight(analysisResult, localValidationIssues = [], flights = []) {
    const violationsByFlightId = new Map();
    const constraintViolations = analysisResult?.constraint_violations || {};
    const previousFlightIdByFlightId = buildPreviousFlightIdByFlightId(flights);

    Object.values(constraintViolations).forEach((violations = []) => {
        toArray(violations).forEach((violation) => {
            if (!isGroundTimeViolation(violation)) {
                return;
            }

            const flightId = violation?.prev_flight_id
                ?? violation?.previous_flight_id
                ?? violation?.prevFlightId
                ?? violation?.previousFlightId
                ?? violation?.flight_id
                ?? violation?.flightId;
            if (!flightId) {
                return;
            }

            addGroundTimeViolationByFlightId(violationsByFlightId, flightId, violation);
        });
    });

    toArray(localValidationIssues).forEach((issue) => {
        const violation = normalizeLocalValidationIssue(issue);
        if (!violation || !isGroundTimeViolation(violation)) {
            return;
        }

        const flightId = getConstraintViolationFlightId(violation);
        addGroundTimeViolationByFlightId(
            violationsByFlightId,
            getGroundTimeViolationOwnerFlightId(violation, flightId, previousFlightIdByFlightId),
            violation,
        );
    });

    return violationsByFlightId;
}

function buildFlightRow(flight, index, sourceLabel, violationsByFlightId = new Map(), overrides = {}, groundTimeViolationsByFlightId = new Map()) {
    const flightId = String(flight.id);
    const useEffectiveTime = Boolean(overrides.useEffectiveTime);
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    const hasGroundTimeViolationOverride = Object.prototype.hasOwnProperty.call(overrides, "groundTimeViolation");
    const groundTimeMinutes = normalizeMinutes(flight.ground_time_minutes);
    const minGroundTimeMinutes = normalizeMinutes(flight.min_ground_time_minutes);
    const explicitGroundTimeViolation = groundTimeViolationsByFlightId.get(flightId)?.[0] ?? null;
    const baseConstraintViolations = overrides.constraintViolations ?? violationsByFlightId.get(flightId) ?? [];
    const groundTimeViolation = hasGroundTimeViolationOverride
        ? overrides.groundTimeViolation
        : explicitGroundTimeViolation ?? buildDerivedGroundTimeViolation(flight, flightId, overrides.nextFlightSummary);
    const constraintViolations = groundTimeViolation && !getGroundTimeViolation(baseConstraintViolations)
        ? [...baseConstraintViolations, groundTimeViolation]
        : baseConstraintViolations;
    const calculationNotes = normalizeCalculationNotes(flight, flightId);
    const constraintViolationsWithNotes = calculationNotes.length
        ? [...constraintViolations, ...calculationNotes]
        : constraintViolations;
    return {
        _rowType: overrides._rowType || "flight",
        id: flight.id || `${sourceLabel}-${index}`,
        rowNumber: overrides.rowNumber ?? index + 1,
        ...overrides,
        flightNumber: flight.flight_number || "--",
        aircraftReg: flight.aircraft_reg || "UNASSIGNED",
        departureAirport: flight.departure_airport || "--",
        arrivalAirport: flight.arrival_airport || "--",
        std: timeInfo.departureTime,
        sta: timeInfo.arrivalTime,
        scheduledStd: flight.std,
        scheduledSta: flight.sta,
        departureTimeSource: timeInfo.departureSourceLabel,
        arrivalTimeSource: timeInfo.arrivalSourceLabel,
        departureTimeLabel: timeInfo.departureLabel,
        arrivalTimeLabel: timeInfo.arrivalLabel,
        usesEffectiveTime: useEffectiveTime,
        flightRouteId: flight.flight_route_id,
        flightRouteFullName: getFlightRouteFullName(flight),
        flightRouteName: getFlightRouteName(flight),
        flightRouteSummary: getFlightRouteSummary(flight),
        flightRouteBlockTimeMinutes: getFlightRouteBlockTime(flight),
        blockTimeMinutes: getFlightBlockTimeMinutes(flight),
        minBlockTimeMinutes: getFlightMinBlockTimeMinutes(flight),
        groundTimeMinutes,
        minGroundTimeMinutes,
        flightDate: flight.flight_date,
        dayOfWeek: flight.day_of_week,
        changeStatus: flight.change_status || "UNCHANGED",
        isFixed: Boolean(flight.is_fixed),
        constraintViolations: constraintViolationsWithNotes,
        groundTimeViolation,
        calculationNotes,
        sourceLabel: overrides.sourceLabel || sourceLabel,
        rawFlight: overrides.rawFlight || flight,
    };
}

function buildRotationIdByFlightId(rotationAnalysis) {
    const rotationIdByFlightId = new Map();
    toArray(rotationAnalysis?.rotations).forEach((rotation, index) => {
        const rotationId = rotation?.rotation_no || `rotation-${index}`;
        toArray(rotation?.flights).forEach((flightRef) => {
            const flightId = typeof flightRef === "object" && flightRef !== null
                ? flightRef.id ?? flightRef.flight_id ?? flightRef.flightId
                : flightRef;
            if (!flightId) {
                return;
            }
            rotationIdByFlightId.set(String(flightId), String(rotationId));
        });
    });
    return rotationIdByFlightId;
}

function getFlightRotationId(flight, rotationIdByFlightId) {
    const flightId = flight?.id;
    const rotationId = flightId ? rotationIdByFlightId.get(String(flightId)) : null;
    return rotationId === undefined || rotationId === null || rotationId === "" ? null : String(rotationId);
}

function getFlightAircraftReg(flight) {
    return String(flight?.aircraft_reg || flight?.aircraftReg || "UNASSIGNED");
}

function hasRotationGapAfter(flight, nextFlight, rotationIdByFlightId) {
    if (!nextFlight || getFlightAircraftReg(flight) !== getFlightAircraftReg(nextFlight)) {
        return false;
    }

    const currentRotationId = getFlightRotationId(flight, rotationIdByFlightId);
    const nextRotationId = getFlightRotationId(nextFlight, rotationIdByFlightId);
    return Boolean(currentRotationId && nextRotationId && currentRotationId !== nextRotationId);
}

function buildChangesByFlightId(pendingChangeSet) {
    const changesByFlightId = new Map();
    toArray(pendingChangeSet?.changes).forEach((change) => {
        const changeFlightId = change?.flightId ?? change?.flight_id;
        if (!changeFlightId) {
            return;
        }

        const flightId = String(changeFlightId);
        const existing = changesByFlightId.get(flightId) || [];
        changesByFlightId.set(flightId, [...existing, change]);
    });
    return changesByFlightId;
}

function applyChangesToFlight(flight, changes, valueKey = "after") {
    const nextFlight = { ...(flight || {}) };
    changes.forEach((change) => {
        if (change?.field) {
            nextFlight[change.field] = change[valueKey];
        }
    });
    return nextFlight;
}

function getRowPreviewDiffValue(row, field) {
    switch (field) {
        case "std":
            return row?.std ?? null;
        case "sta":
            return row?.sta ?? null;
        case "block_time_minutes":
            return row?.blockTimeMinutes ?? null;
        case "ground_time_minutes":
            return row?.groundTimeMinutes ?? null;
        default:
            return null;
    }
}

function buildPreviewDiff(field, before, after) {
    if (field === "std" || field === "sta") {
        if (valuesEqual(field, before, after)) {
            return null;
        }
        return {
            field,
            before: before ?? null,
            after: after ?? null,
            deltaMinutes: calculateDateTimeDiffMinutes(before, after),
        };
    }

    const beforeMinutes = normalizeMinutes(before);
    const afterMinutes = normalizeMinutes(after);
    if (beforeMinutes === afterMinutes) {
        return null;
    }

    return {
        field,
        before: beforeMinutes,
        after: afterMinutes,
        deltaMinutes: beforeMinutes !== null && afterMinutes !== null ? afterMinutes - beforeMinutes : null,
    };
}

function buildPreviewDiffs(beforeRow, afterRow) {
    return TIMETABLE_PREVIEW_DIFF_FIELDS.reduce((diffs, field) => {
        const diff = buildPreviewDiff(field, getRowPreviewDiffValue(beforeRow, field), getRowPreviewDiffValue(afterRow, field));
        if (diff) {
            diffs[field] = diff;
        }
        return diffs;
    }, {});
}

function hasPreviewDiffs(previewDiffs = {}) {
    return Object.keys(previewDiffs || {}).length > 0;
}

function buildPreviewRow(row, changes) {
    return buildFlightRow(row.rawFlight || {}, 0, row.sourceLabel, new Map(), {
        _rowType: "change-preview",
        _previewRole: "before",
        id: `preview-${row.id}`,
        rowNumber: row.rowNumber,
        sourceFlightId: row.id,
        changes,
        constraintViolations: row.constraintViolations || [],
        groundTimeViolation: row.groundTimeViolation || null,
        aircraftAvailabilityValidationPending: Boolean(row.aircraftAvailabilityValidationPending),
        sourceLabel: row.sourceLabel,
    });
}

function buildPreviewValidationMaps(previewFlights = [], airportConstraintsMap = {}, aircraftAvailabilityWindowsByRegistration = {}) {
    const previewFlightsMap = new Map(previewFlights.map((flight) => [String(flight.id), flight]));
    const previousFlightIdByFlightId = buildPreviousFlightIdByFlightId(previewFlights);
    const previewIssues = validateAllFlightConstraints(previewFlightsMap, airportConstraintsMap, aircraftAvailabilityWindowsByRegistration);
    const previewViolationsByFlightId = collectConstraintViolationsByFlight(null, previewIssues);
    const previewGroundTimeViolationsByFlightId = new Map();

    previewViolationsByFlightId.forEach((violations, flightId) => {
        const violation = getGroundTimeViolation(violations);
        if (violation) {
            previewGroundTimeViolationsByFlightId.set(getGroundTimeViolationOwnerFlightId(violation, flightId, previousFlightIdByFlightId), violation);
        }
    });

    return {
        previewViolationsByFlightId,
        previewGroundTimeViolationsByFlightId,
    };
}

function mapFlightsToRows(flights = [], sourceLabel, violationsByFlightId = new Map(), { rotationAnalysis = null, pendingChangeSet = null, airportConstraintsMap = {}, aircraftAvailabilityWindowsByRegistration = {}, isAircraftAvailabilityValidationPending = false, groundTimeViolationsByFlightId = new Map(), useEffectiveTime = false } = {}) {
    // GT is already calculated upstream (Context layer) on full dataset.
    // Only recalculate for preview flights (pending change set) which need updated GT after applying changes.
    const visibleFlights = flights.filter((flight) => !isDeletedFlight(flight));
    const rotationIdByFlightId = buildRotationIdByFlightId(rotationAnalysis);
    const changesByFlightId = buildChangesByFlightId(pendingChangeSet);
    const previewFlightsById = new Map(
        withCalculatedFlightTimings(
            visibleFlights.map((flight) => applyChangesToFlight(flight, changesByFlightId.get(String(flight.id)) || [], "after")),
            airportConstraintsMap,
        ).map((flight) => [String(flight.id), applyChangesToFlight(flight, changesByFlightId.get(String(flight.id)) || [], "after")]),
    );
    const { previewViolationsByFlightId, previewGroundTimeViolationsByFlightId } = changesByFlightId.size
        ? buildPreviewValidationMaps(Array.from(previewFlightsById.values()), airportConstraintsMap, aircraftAvailabilityWindowsByRegistration)
        : { previewViolationsByFlightId: new Map(), previewGroundTimeViolationsByFlightId: new Map() };
    const rows = [];

    visibleFlights.forEach((flight, index) => {
        const nextFlight = visibleFlights[index + 1];
        const rowSpacingOverrides = {
            ...(hasRotationGapAfter(flight, nextFlight, rotationIdByFlightId) ? { _hasRotationGapAfter: true } : {}),
            nextFlightSummary: summarizeAdjacentFlight(nextFlight),
            aircraftAvailabilityValidationPending: Boolean(isAircraftAvailabilityValidationPending),
            useEffectiveTime,
        };
        const baseRow = buildFlightRow(flight, index, sourceLabel, violationsByFlightId, rowSpacingOverrides, groundTimeViolationsByFlightId);
        if (!changesByFlightId.size) {
            rows.push(baseRow);
            return;
        }

        const previewChanges = changesByFlightId.get(String(baseRow.id));
        const previewFlightId = String(flight.id);
        const previewFlight = previewFlightsById.get(previewFlightId) || applyChangesToFlight(flight, previewChanges || [], "after");
        const previewViolations = previewViolationsByFlightId.get(previewFlightId) || [];
        const previewAfterRow = buildFlightRow(previewFlight, index, sourceLabel, violationsByFlightId, {
            ...rowSpacingOverrides,
            _previewRole: "after",
            changes: previewChanges || [],
            constraintViolations: previewViolations,
            groundTimeViolation: previewGroundTimeViolationsByFlightId.get(previewFlightId) || null,
            sourceLabel,
        }, groundTimeViolationsByFlightId);
        const previewDiffs = buildPreviewDiffs(baseRow, previewAfterRow);
        if (previewChanges?.length || hasPreviewDiffs(previewDiffs)) {
            previewAfterRow.previewDiffs = previewDiffs;
            rows.push(buildPreviewRow(baseRow, previewChanges || []));
            rows.push(previewAfterRow);
        } else {
            rows.push(baseRow);
        }
    });

    return rows;
}

function getKnownAirportCode(value) {
    const code = normalizeAirportCode(value);
    return code && code !== "--" ? code : null;
}

function buildMetrics(rows = []) {
    const aircraftSet = new Set();
    const routeSet = new Set();
    const airportSet = new Set();
    const stcCounts = new Map();
    const rotationSet = new Set();
    let totalBlockMinutes = 0;
    let totalGroundMinutes = 0;
    let groundTimeSegmentCount = 0;

    const flightRows = rows.filter((row) => row?._rowType === "flight");
    flightRows.forEach((row) => {
        const aircraftReg = normalizeAircraftIdentifier(row.aircraftReg);
        if (aircraftReg) {
            aircraftSet.add(aircraftReg);
        }

        const departureAirport = getKnownAirportCode(row.departureAirport);
        const arrivalAirport = getKnownAirportCode(row.arrivalAirport);
        if (departureAirport) {
            airportSet.add(departureAirport);
        }
        if (arrivalAirport) {
            airportSet.add(arrivalAirport);
        }
        if (departureAirport && arrivalAirport) {
            routeSet.add(`${departureAirport}-${arrivalAirport}`);
        }

        const blockMinutes = normalizeMinutes(row.blockTimeMinutes);
        if (blockMinutes !== null) {
            totalBlockMinutes += blockMinutes;
        }

        const groundMinutes = normalizeMinutes(row.groundTimeMinutes);
        if (groundMinutes !== null) {
            totalGroundMinutes += groundMinutes;
            groundTimeSegmentCount += 1;
        }

        const stcType = String(row.rawFlight?.stc_type || row.stcType || "未设置").trim().toUpperCase() || "未设置";
        stcCounts.set(stcType, (stcCounts.get(stcType) || 0) + 1);

        const rotationId = row.rawFlight?.rotation_no ?? row.rawFlight?.rotationNo ?? row.rawFlight?.rotation_id ?? row.rotationNo;
        if (rotationId !== undefined && rotationId !== null && String(rotationId).trim()) {
            rotationSet.add(String(rotationId).trim());
        }
    });

    const airportCodes = Array.from(airportSet).sort((left, right) => left.localeCompare(right));
    const stcDistribution = Array.from(stcCounts.entries())
        .map(([type, count]) => ({ type, count }))
        .sort((left, right) => right.count - left.count || left.type.localeCompare(right.type));

    return {
        flightCount: flightRows.length,
        aircraftCount: aircraftSet.size,
        routeCount: routeSet.size,
        totalBlockMinutes,
        totalBlockTime: formatMinutesAsHHMM(totalBlockMinutes),
        totalGroundMinutes: groundTimeSegmentCount ? totalGroundMinutes : null,
        totalGroundTime: groundTimeSegmentCount ? formatMinutesAsHHMM(totalGroundMinutes) : "--",
        groundTimeSegmentCount,
        airportCount: airportCodes.length,
        airportCodes,
        stcDistribution,
        rotationCount: rotationSet.size || null,
    };
}

function formatPatternDateTime(value) {
    if (!value) {
        return "--";
    }

    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) {
        return "--";
    }

    return parsed.format("MM-DD HH:mm");
}

function getDisplayDepartureTime(flight, useEffectiveTime = false) {
    return getFlightTimeDisplayInfo(flight, { useEffectiveTime }).departureTime;
}

function getDisplayArrivalTime(flight, useEffectiveTime = false) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    return timeInfo.arrivalTime || timeInfo.departureTime;
}

function getFlightSortTime(flight, useEffectiveTime = false) {
    const parsed = dayjs.utc(getDisplayDepartureTime(flight, useEffectiveTime) || getDisplayArrivalTime(flight, useEffectiveTime));
    return parsed.isValid() ? parsed.valueOf() : Number.MAX_SAFE_INTEGER;
}

function sortFlightsByDisplayTime(flights = [], useEffectiveTime = false) {
    return [...flights].sort((left, right) => getFlightSortTime(left, useEffectiveTime) - getFlightSortTime(right, useEffectiveTime));
}

function toIsoString(value) {
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.toISOString() : null;
}

function buildCalendarTimeWindow(start, end) {
    const windowStart = toIsoString(start);
    const windowEnd = toIsoString(end);
    return windowStart && windowEnd ? { start: windowStart, end: windowEnd } : null;
}

function getMondayWeekStart(value) {
    // 周分桶必须按 UTC 日界计算，不响应设备本地时区
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) {
        return null;
    }

    const day = parsed.day();
    const offsetDays = day === 0 ? 6 : day - 1;
    return parsed.subtract(offsetDays, "day").startOf("day");
}

function getWeeklyCalendarBounds(flights = [], draft = null, useEffectiveTime = false) {
    const sortedFlights = sortFlightsByDisplayTime(flights, useEffectiveTime).filter((flight) => dayjs.utc(getDisplayDepartureTime(flight, useEffectiveTime) || getDisplayArrivalTime(flight, useEffectiveTime)).isValid());
    const firstFlight = sortedFlights[0];
    const lastFlight = sortedFlights[sortedFlights.length - 1];
    const rangeStartSource = draft?.period_start || getDisplayDepartureTime(firstFlight, useEffectiveTime) || getDisplayArrivalTime(firstFlight, useEffectiveTime);
    const rangeEndSource = draft?.period_end || getDisplayArrivalTime(lastFlight, useEffectiveTime) || getDisplayDepartureTime(lastFlight, useEffectiveTime);
    const firstWeekStart = getMondayWeekStart(rangeStartSource);
    const lastWeekStart = getMondayWeekStart(rangeEndSource);

    if (!firstWeekStart || !lastWeekStart) {
        return null;
    }

    return { firstWeekStart, lastWeekStart };
}

function buildFlightsTimeWindow(flights = [], fallbackWindow = null, useEffectiveTime = false) {
    const validTimes = toArray(flights)
        .flatMap((flight) => [getDisplayDepartureTime(flight, useEffectiveTime), getDisplayArrivalTime(flight, useEffectiveTime)])
        .map((value) => dayjs.utc(value))
        .filter((value) => value.isValid());

    if (!validTimes.length) {
        return fallbackWindow;
    }

    const minTime = Math.min(...validTimes.map((value) => value.valueOf()));
    const maxTime = Math.max(...validTimes.map((value) => value.valueOf()));
    return {
        start: dayjs.utc(minTime).subtract(SEGMENT_TIME_MARGIN_HOURS, "hour").toISOString(),
        end: dayjs.utc(maxTime).add(SEGMENT_TIME_MARGIN_HOURS, "hour").toISOString(),
    };
}

function buildPatternFlightToken(flight, index, useEffectiveTime = false) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    return {
        key: flight.id || `${flight.flight_number || "flight"}-${index}`,
        flightNumber: flight.flight_number || "--",
        route: `${flight.departure_airport || "--"} -> ${flight.arrival_airport || "--"}`,
        timeRange: useEffectiveTime
            ? `${timeInfo.departureSourceLabel} ${formatPatternDateTime(timeInfo.departureTime)} - ${timeInfo.arrivalSourceLabel} ${formatPatternDateTime(timeInfo.arrivalTime || timeInfo.departureTime)}`
            : `${formatPatternDateTime(timeInfo.departureTime)} - ${formatPatternDateTime(timeInfo.arrivalTime || timeInfo.departureTime)}`,
        aircraftReg: flight.aircraft_reg || "UNASSIGNED",
        changeStatus: flight.change_status || "UNCHANGED",
        isFixed: Boolean(flight.is_fixed),
    };
}

function buildPatternRow(rowKey, rowLabel, flights = [], emptyDescription, useEffectiveTime = false) {
    return {
        key: rowKey,
        label: rowLabel,
        metrics: {
            flightCount: flights.length,
        },
        flights: flights.map((flight, index) => buildPatternFlightToken(flight, index, useEffectiveTime)),
        emptyDescription: emptyDescription || "当前分段没有航班实例。",
    };
}

function buildPatternOverview(flights = [], activeSourceTrack, patternType) {
    const aircraftCount = new Set(flights.map((flight) => flight.aircraft_reg || "UNASSIGNED")).size;

    return {
        sourceTrackLabel: activeSourceTrack?.label || CURRENT_PLAN_LABEL,
        patternType,
        flightCount: flights.length,
        aircraftCount,
    };
}

function buildWeeklySegments(flights, activeSourceTrack, draft = null, useEffectiveTime = false) {
    const bounds = getWeeklyCalendarBounds(flights, draft, useEffectiveTime);
    if (!bounds) {
        return [];
    }

    const weeklyBuckets = {};
    for (let cursor = bounds.firstWeekStart; !cursor.isAfter(bounds.lastWeekStart, "day"); cursor = cursor.add(7, "day")) {
        weeklyBuckets[cursor.format("YYYY-MM-DD")] = {
            key: cursor.format("YYYY-MM-DD"),
            weekStart: cursor,
            flights: [],
        };
    }

    sortFlightsByDisplayTime(flights, useEffectiveTime).forEach((flight) => {
        const bucketStart = getMondayWeekStart(getDisplayDepartureTime(flight, useEffectiveTime) || getDisplayArrivalTime(flight, useEffectiveTime));
        const bucketKey = bucketStart?.format("YYYY-MM-DD");
        if (!bucketKey) {
            return;
        }

        if (!weeklyBuckets[bucketKey]) {
            weeklyBuckets[bucketKey] = {
                key: bucketKey,
                weekStart: bucketStart,
                flights: [],
            };
        }

        weeklyBuckets[bucketKey].flights.push(flight);
    });

    return Object.values(weeklyBuckets).sort((left, right) => left.weekStart.valueOf() - right.weekStart.valueOf()).map((bucket, index) => {
        const weekEnd = bucket.weekStart.add(6, "day");
        const timeWindow = buildCalendarTimeWindow(bucket.weekStart.startOf("day"), weekEnd.endOf("day"));
        return {
            key: bucket.key,
            title: `Week ${index + 1}`,
            summary: `${bucket.weekStart.format("YYYY-MM-DD")} -> ${weekEnd.format("YYYY-MM-DD")}`,
            timeWindow,
            focusLabel: `Week ${index + 1} · ${bucket.weekStart.format("MM-DD")} -> ${weekEnd.format("MM-DD")}`,
            metrics: {
                flightCount: bucket.flights.length,
                aircraftCount: new Set(bucket.flights.map((flight) => flight.aircraft_reg || "UNASSIGNED")).size,
            },
            rows: [buildPatternRow(activeSourceTrack?.value || DRAFT_CURRENT_TRACK_KEY, activeSourceTrack?.label || CURRENT_PLAN_LABEL, bucket.flights, undefined, useEffectiveTime)],
        };
    });
}

function buildRotationSegments(flights, activeSourceTrack, useEffectiveTime = false) {
    const aircraftBuckets = sortFlightsByDisplayTime(flights, useEffectiveTime).reduce((accumulator, flight) => {
        const aircraftReg = flight.aircraft_reg || "UNASSIGNED";
        if (!accumulator[aircraftReg]) {
            accumulator[aircraftReg] = [];
        }
        accumulator[aircraftReg].push(flight);
        return accumulator;
    }, {});

    return Object.entries(aircraftBuckets)
        .map(([aircraftReg, aircraftFlights]) => {
            const sortedFlights = sortFlightsByDisplayTime(aircraftFlights, useEffectiveTime);
            const firstFlight = sortedFlights[0];
            const lastFlight = sortedFlights[sortedFlights.length - 1];
            const firstDeparture = getDisplayDepartureTime(firstFlight, useEffectiveTime);
            const lastArrival = getDisplayArrivalTime(lastFlight, useEffectiveTime);

            return {
                key: aircraftReg,
                title: aircraftReg,
                summary: `${formatPatternDateTime(firstDeparture)} -> ${formatPatternDateTime(lastArrival)}`,
                timeWindow: buildFlightsTimeWindow(sortedFlights, null, useEffectiveTime),
                focusLabel: `${aircraftReg} · ${formatPatternDateTime(firstDeparture)} -> ${formatPatternDateTime(lastArrival)}`,
                metrics: {
                    flightCount: aircraftFlights.length,
                    aircraftCount: 1,
                },
                rows: [buildPatternRow(activeSourceTrack?.value || aircraftReg, activeSourceTrack?.label || CURRENT_PLAN_LABEL, sortedFlights, undefined, useEffectiveTime)],
            };
        })
        .sort((left, right) => (left.timeWindow?.start || "").localeCompare(right.timeWindow?.start || ""));
}

function buildDemandSegments(flights, draft, activeSourceTrack, useEffectiveTime = false) {
    const demands = getScheduleDraftTransportDemands(draft);
    if (demands.length) {
        return demands.map((demand, index) => {
            const departureAirport = normalizeAirportCode(demand?.departure_airport || demand?.origin || demand?.origin_airport || demand?.dep) || "--";
            const arrivalAirport = normalizeAirportCode(demand?.arrival_airport || demand?.destination || demand?.destination_airport || demand?.arr) || "--";
            const routeKey = `${departureAirport}-${arrivalAirport}`;
            const routeFlights = flights.filter((flight) => {
                const flightDeparture = normalizeAirportCode(flight?.departure_airport);
                const flightArrival = normalizeAirportCode(flight?.arrival_airport);
                return flightDeparture === departureAirport && (!arrivalAirport || arrivalAirport === "--" || flightArrival === arrivalAirport);
            });
            const sortedFlights = sortFlightsByDisplayTime(routeFlights, useEffectiveTime);
            const requiredCount = Math.max(Number(demand?.frequency || 1), 1);

            return {
                key: `${routeKey}-${demand?.id || index}`,
                title: routeKey.replace("-", " -> "),
                summary: `${routeFlights.length} / ${requiredCount} 个需求匹配航班`,
                timeWindow: buildFlightsTimeWindow(sortedFlights, null, useEffectiveTime),
                focusLabel: `${routeKey.replace("-", " -> ")} · ${routeFlights.length} 个航班`,
                metrics: {
                    flightCount: routeFlights.length,
                    aircraftCount: new Set(routeFlights.map((flight) => flight.aircraft_reg || "UNASSIGNED")).size,
                },
                rows: [
                    buildPatternRow(
                        `${routeKey}:${activeSourceTrack?.value || DRAFT_CURRENT_TRACK_KEY}`,
                        activeSourceTrack?.label || CURRENT_PLAN_LABEL,
                        sortedFlights,
                        "当前需求航线没有匹配航班实例。",
                        useEffectiveTime,
                    ),
                ],
            };
        });
    }

    const groupedByRoute = flights.reduce((accumulator, flight) => {
        const routeKey = `${flight.departure_airport || "--"}-${flight.arrival_airport || "--"}`;
        if (!accumulator[routeKey]) {
            accumulator[routeKey] = [];
        }
        accumulator[routeKey].push(flight);
        return accumulator;
    }, {});

    const routeSegments = Object.entries(groupedByRoute)
        .map(([routeKey, routeFlights]) => {
            const sortedFlights = sortFlightsByDisplayTime(routeFlights, useEffectiveTime);
            return {
                key: routeKey,
                title: routeKey.replace("-", " -> "),
                summary: `${routeFlights.length} 个航班`,
                timeWindow: buildFlightsTimeWindow(sortedFlights, null, useEffectiveTime),
                focusLabel: `${routeKey.replace("-", " -> ")} · ${routeFlights.length} 个航班`,
                metrics: {
                    flightCount: routeFlights.length,
                    aircraftCount: new Set(routeFlights.map((flight) => flight.aircraft_reg || "UNASSIGNED")).size,
                },
                rows: [
                    buildPatternRow(
                        `${routeKey}:${activeSourceTrack?.value || DRAFT_CURRENT_TRACK_KEY}`,
                        activeSourceTrack?.label || CURRENT_PLAN_LABEL,
                        sortedFlights,
                        "当前分段没有航班实例。",
                        useEffectiveTime,
                    ),
                ],
            };
        })
        .sort((left, right) => (left.timeWindow?.start || "").localeCompare(right.timeWindow?.start || ""));

    if (routeSegments.length) {
        return routeSegments;
    }

    return [
        {
            key: "demand-placeholder",
            title: draft?.name || "航班计划",
            summary: "当前没有可展示的需求分段。",
            metrics: {
                flightCount: 0,
                aircraftCount: 0,
            },
            rows: [
                {
                    key: "demand-placeholder-row",
                    label: "需求分段",
                    metrics: { flightCount: 0 },
                    flights: [],
                    emptyDescription: "当前分段没有航班实例。",
                },
            ],
        },
    ];
}

/**
 * 构建 Timetable 与 Pattern 共用的源轨选项列表。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @param {Array<string|number>} visibleReferenceTrackIds - 已展开的参考轨 ID。
 * @param {Array<Object>} externalReferenceTracks - 已挂载外部参考轨。
 * @param {Array<string>} referenceTrackKeys - 用户排序后的参考轨 key。
 * @returns {Array<Object>} 源轨选择项。
 */
export function buildScheduleDraftSourceTrackOptions(draft, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = []) {
    const defaultTrack = getDefaultEditingTrack(draft);
    const editingTracks = getEditingTracks(draft);
    const options = [
        {
            value: DRAFT_CURRENT_TRACK_KEY,
            label: defaultTrack?.name || CURRENT_PLAN_LABEL,
            kind: "draft",
            description: "当前编辑轨的航班实例",
            track: defaultTrack,
        },
    ];

    editingTracks
        .filter((track) => String(track.id) !== String(defaultTrack?.id))
        .forEach((track) => {
            options.push({
                value: buildScheduleTrackKey(track.id),
                label: getTrackDisplayName(track),
                kind: "editing",
                description: "可编辑轨的航班实例",
                track,
            });
        });

    const tracksById = new Map(getReferenceDisplayTracks(draft).map((track) => [String(track.id), track]));
    const externalByKey = new Map(toArray(externalReferenceTracks).map((referenceSource) => [referenceSource.trackKey, referenceSource]));
    const orderedReferenceKeys = referenceTrackKeys.length
        ? referenceTrackKeys
        : visibleReferenceTrackIds.map(buildScheduleTrackKey);

    orderedReferenceKeys.forEach((trackKey) => {
        if (isScheduleTrackKey(trackKey)) {
            const track = tracksById.get(parseTrackKey(trackKey).id);
            if (!track) {
                return;
            }
            const agentTrack = isAgentTrack(track);

            options.push({
                value: buildScheduleTrackKey(track.id),
                label: agentTrack ? `AI 方案 · ${getAgentTrackDisplayLabel(track)}` : getTrackDisplayName(track),
                kind: "track",
                description: agentTrack ? getAgentEvaluationSummary(track) : track.summary || "只读参考轨",
                track,
                usesEffectiveTime: !agentTrack && isExecutionReferenceSource(track),
            });
            return;
        }

        const referenceSource = externalByKey.get(trackKey);
        if (!referenceSource) {
            return;
        }

        options.push({
            value: trackKey,
            label: referenceSource.source_label || referenceSource.reference_id,
            kind: "reference",
            description: referenceSource.source_id ? `Source ID: ${referenceSource.source_id}` : "外部来源参考轨",
            referenceSource,
            usesEffectiveTime: isExecutionReferenceSource(referenceSource),
        });
    });

    return options;
}

export function getDefaultEditingTrackId(draft) {
    return getDefaultEditingTrack(draft)?.id || draft?.default_track_id || null;
}

export function resolveScheduleDraftEditingTrackId(draft, trackId, fallbackTrackId = null) {
    const editingTracks = getEditingTracks(draft);
    const matchedTrack = editingTracks.find((track) => String(track.id) === String(trackId));
    if (matchedTrack) {
        return matchedTrack.id;
    }

    const fallbackTrack = editingTracks.find((track) => String(track.id) === String(fallbackTrackId));
    return fallbackTrack?.id || getDefaultEditingTrackId(draft);
}

export function resolveEditingTrackIdFromSourceTrackKey(draft, sourceTrackKey, fallbackTrackId = null) {
    if (sourceTrackKey === DRAFT_CURRENT_TRACK_KEY) {
        return getDefaultEditingTrackId(draft);
    }

    if (isScheduleTrackKey(sourceTrackKey)) {
        return resolveScheduleDraftEditingTrackId(draft, parseTrackKey(sourceTrackKey).id, fallbackTrackId);
    }

    return resolveScheduleDraftEditingTrackId(draft, fallbackTrackId);
}

export function buildSourceTrackKeyForEditingTrack(draft, trackId) {
    const editingTrackId = resolveScheduleDraftEditingTrackId(draft, trackId);
    if (!editingTrackId || String(editingTrackId) === String(getDefaultEditingTrackId(draft))) {
        return DRAFT_CURRENT_TRACK_KEY;
    }
    return buildScheduleTrackKey(editingTrackId);
}

/**
 * 解析当前 sourceTrack 对应的来源元数据与航班集合。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @param {string} sourceTrack - 当前源轨 key。
 * @param {Array<string|number>} visibleReferenceTrackIds - 已展开的参考轨 ID。
 * @param {Array<Object>} externalReferenceTracks - 已挂载外部参考轨。
 * @param {Array<string>} referenceTrackKeys - 用户排序后的参考轨 key。
 * @returns {Object|null} 源轨元数据，包含 flights。
 */
export function resolveScheduleDraftSourceTrackMeta(draft, sourceTrack, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = []) {
    const sourceTrackOptions = buildScheduleDraftSourceTrackOptions(draft, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys);
    const matchedOption = sourceTrackOptions.find((option) => option.value === sourceTrack) || sourceTrackOptions[0] || null;

    if (!matchedOption) {
        return null;
    }

    if (matchedOption.kind === "editing" || matchedOption.kind === "track") {
        return {
            ...matchedOption,
            flights: applyReferenceDateShift(getTrackFlights(matchedOption.track), matchedOption.track),
            usesEffectiveTime: matchedOption.kind === "track" && Boolean(matchedOption.usesEffectiveTime),
        };
    }

    if (matchedOption.kind === "reference") {
        return {
            ...matchedOption,
            flights: getExternalReferenceFlights(matchedOption.referenceSource),
            usesEffectiveTime: Boolean(matchedOption.usesEffectiveTime),
        };
    }

    return {
        ...matchedOption,
        flights: matchedOption.track ? applyReferenceDateShift(getTrackFlights(matchedOption.track), matchedOption.track) : getDraftFlights(draft),
    };
}

/**
 * 构建 Timetable 区块使用的结构化表格模型。
 * @param {Object} params - 模型输入。
 * @param {Object|null} params.draft - ScheduleDraft 详情。
 * @param {Array<string|number>} params.visibleReferenceTrackIds - 已展开的参考轨 ID。
 * @param {Array<Object>} params.externalReferenceTracks - 已挂载外部参考轨。
 * @param {Array<string>} params.referenceTrackKeys - 用户排序后的参考轨 key。
 * @param {string} params.timetableTab - 当前 Timetable 源轨 key。
 * @returns {Object} Timetable ViewModel。
 */
export function buildScheduleDraftTimetableModel({ draft, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = [], timetableTab = DRAFT_CURRENT_TRACK_KEY, analysisResult = null, pendingChangeSet = null, airportConstraintsMap = {}, aircraftAvailabilityWindowsByRegistration = {}, isAircraftAvailabilityValidationPending = false, localValidationIssues = [] }) {
    const sourceTrackOptions = buildScheduleDraftSourceTrackOptions(draft, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys);
    const activeSourceTrack = resolveScheduleDraftSourceTrackMeta(draft, timetableTab, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys);
    const isEditableTrack = activeSourceTrack?.kind === "draft" || activeSourceTrack?.kind === "editing";
    const isReadOnlyTrack = !isEditableTrack;
    const useEffectiveTime = isReadOnlyTrack && Boolean(activeSourceTrack?.usesEffectiveTime);
    const activeFlights = useEffectiveTime ? sortFlightsByDisplayTime(activeSourceTrack?.flights || [], true) : activeSourceTrack?.flights || [];
    const violationsByFlightId = isEditableTrack ? collectConstraintViolationsByFlight(analysisResult, localValidationIssues) : new Map();
    const groundTimeViolationsByFlightId = isEditableTrack ? collectGroundTimeViolationsByFlight(analysisResult, localValidationIssues, activeFlights) : new Map();

    const rows = mapFlightsToRows(activeFlights, activeSourceTrack?.label || CURRENT_PLAN_LABEL, violationsByFlightId, {
        rotationAnalysis: isEditableTrack ? analysisResult?.rotation_analysis : null,
        pendingChangeSet: isEditableTrack ? pendingChangeSet : null,
        airportConstraintsMap,
        aircraftAvailabilityWindowsByRegistration,
        isAircraftAvailabilityValidationPending,
        groundTimeViolationsByFlightId,
        useEffectiveTime,
    });

    return {
        sourceTrackOptions,
        activeSourceTrack,
        rows,
        metrics: buildMetrics(rows),
        title: `${activeSourceTrack?.label || CURRENT_PLAN_LABEL} Timetable`,
        description: isEditableTrack
            ? "以结构化表格查看并编辑当前轨中的航班实例、机号分布与班期字段。"
            : `当前正在查看 ${activeSourceTrack?.description || activeSourceTrack?.label || "参考轨"} 的只读结构化表格。`,
        emptyDescription: isReadOnlyTrack ? "当前没有可用于参考轨表格的数据。" : "当前轨还没有可展示的航班。",
        mode: "table",
        usesEffectiveTime: useEffectiveTime,
    };
}

/**
 * 构建 Pattern 观察区使用的分段模型。
 * @param {Object} params - 模型输入。
 * @param {Object|null} params.draft - ScheduleDraft 详情。
 * @param {Array<string|number>} params.visibleReferenceTrackIds - 已展开的参考轨 ID。
 * @param {Array<Object>} params.externalReferenceTracks - 已挂载外部参考轨。
 * @param {Array<string>} params.referenceTrackKeys - 用户排序后的参考轨 key。
 * @param {string} params.sourceTrack - 当前源轨 key。
 * @param {string} params.patternType - Pattern 观察类型。
 * @returns {Object} Pattern ViewModel。
 */
export function buildScheduleDraftPatternModel({ draft, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = [], sourceTrack, patternType = "weekly" }) {
    const activeSourceTrack = resolveScheduleDraftSourceTrackMeta(draft, sourceTrack, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys);
    const useEffectiveTime = Boolean(activeSourceTrack?.usesEffectiveTime);
    const flights = useEffectiveTime ? sortFlightsByDisplayTime(activeSourceTrack?.flights || [], true) : activeSourceTrack?.flights || [];

    const canBuildWeeklyCalendar = patternType === "weekly" && Boolean(draft?.period_start || draft?.period_end);

    if (!flights.length && !canBuildWeeklyCalendar && patternType !== "demand") {
        return {
            title: "Pattern 观察区",
            description: "当前没有可用于 Pattern 模式的航班数据。",
            segments: [],
            overview: buildPatternOverview([], activeSourceTrack, patternType),
        };
    }

    if (patternType === "rotation") {
        return {
            title: "Pattern · Rotation Segments",
            description: "按飞机注册号查看航班分段。",
            segments: buildRotationSegments(flights, activeSourceTrack, useEffectiveTime),
            overview: buildPatternOverview(flights, activeSourceTrack, patternType),
        };
    }

    if (patternType === "demand") {
        return {
            title: "Pattern · Demand Segments",
            description: "按航线查看需求相关航班分段。",
            segments: buildDemandSegments(flights, draft, activeSourceTrack, useEffectiveTime),
            overview: buildPatternOverview(flights, activeSourceTrack, patternType),
        };
    }

    return {
        title: "Pattern · Weekly Segments",
        description: "按固定自然周查看航班分布，周一为起始日。",
        segments: buildWeeklySegments(flights, activeSourceTrack, draft, useEffectiveTime),
        overview: buildPatternOverview(flights, activeSourceTrack, patternType),
    };
}

const scheduleDraftWorkspaceModelService = {
    buildScheduleDraftSourceTrackOptions,
    getDefaultEditingTrackId,
    resolveScheduleDraftEditingTrackId,
    resolveEditingTrackIdFromSourceTrackKey,
    buildSourceTrackKeyForEditingTrack,
    resolveScheduleDraftSourceTrackMeta,
    buildScheduleDraftTimetableModel,
    buildScheduleDraftPatternModel,
};

export default scheduleDraftWorkspaceModelService;
